import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "@/store/DataContext";
import EmptyState from "@/components/base/EmptyState";
import Badge from "@/components/base/Badge";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { daysUntil, formatDate, money, number } from "@/lib/format";
import type { StockRow } from "@/lib/types";

export default function Inventario() {
  const { stock, loading, deleteProduct } = useData();
  const navigate = useNavigate();
  const [pending, setPending] = useState<StockRow | null>(null);

  const totals = useMemo(() => {
    const value = stock.reduce((s, r) => s + r.inventoryValue, 0);
    const units = stock.reduce((s, r) => s + r.quantity, 0);
    const potential = stock.reduce((s, r) => s + r.potentialProfit, 0);
    return { value, units, potential };
  }, [stock]);

  const handleConfirm = async () => {
    if (!pending) return;
    const product = pending;
    setPending(null);
    await deleteProduct(product.productId);
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  return (
    <div className="fr-animate space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-foreground-950">Inventario</h1>
          <p className="mt-0.5 text-[13px] text-foreground-500">Productos, cantidades, costos y vencimientos</p>
        </div>
        {stock.length > 0 ? (
          <button
            type="button"
            onClick={() => navigate("/compras/nueva")}
            className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-card active:scale-[0.98]"
          >
            <i className="ri-add-line text-xl" />
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-3xl border border-background-200/70 bg-background-50 p-3 shadow-card">
          <p className="text-[11px] text-foreground-500">Valor</p>
          <p className="tabular-nums mt-1 text-[15px] font-semibold text-foreground-950">
            {money(totals.value, "DOP")}
          </p>
        </div>
        <div className="rounded-3xl border border-background-200/70 bg-background-50 p-3 shadow-card">
          <p className="text-[11px] text-foreground-500">Unidades</p>
          <p className="tabular-nums mt-1 text-[15px] font-semibold text-foreground-950">
            {number(totals.units, 0)}
          </p>
        </div>
        <div className="rounded-3xl border border-background-200/70 bg-background-50 p-3 shadow-card">
          <p className="text-[11px] text-foreground-500">Ganancia est.</p>
          <p className="tabular-nums mt-1 text-[15px] font-semibold text-primary-700">
            {money(totals.potential, "DOP")}
          </p>
        </div>
      </div>

      {stock.length === 0 ? (
        <EmptyState
          icon="ri-box-3-line"
          title="Sin productos en inventario"
          description="Registra una compra para empezar a controlar tus productos."
          actionLabel="Agregar producto"
          actionIcon="ri-add-line"
          onAction={() => navigate("/compras/nueva")}
        />
      ) : (
        <div className="space-y-3">
          {stock.map((row) => {
            const days = daysUntil(row.nextExpiration);
            const low = row.quantity <= 3;
            return (
              <div
                key={row.productId}
                className="rounded-3xl border border-background-200/70 bg-background-50 p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-semibold text-foreground-950">
                      {row.productName}
                    </p>
                    <p className="mt-0.5 text-[12px] text-foreground-500">{row.category}</p>
                  </div>
                  <div className="flex flex-none items-center gap-2">
                    <Badge tone={low ? "danger" : "primary"}>
                      {number(row.quantity, 0)} {row.unit}
                    </Badge>
                    <button
                      type="button"
                      aria-label="Eliminar producto"
                      onClick={() => setPending(row)}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-400 active:bg-red-50 active:text-red-500"
                    >
                      <i className="ri-delete-bin-line text-[17px]" />
                    </button>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-2xl bg-background-100 py-2">
                    <p className="text-[10px] text-foreground-500">Costo prom.</p>
                    <p className="tabular-nums text-[13px] font-semibold text-foreground-900">
                      {money(row.avgCost, row.currency)}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-background-100 py-2">
                    <p className="text-[10px] text-foreground-500">Precio venta</p>
                    <p className="tabular-nums text-[13px] font-semibold text-foreground-900">
                      {money(row.bestSalePrice, row.currency)}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-primary-50 py-2">
                    <p className="text-[10px] text-foreground-500">Valor</p>
                    <p className="tabular-nums text-[13px] font-semibold text-primary-700">
                      {money(row.inventoryValue, row.currency)}
                    </p>
                  </div>
                </div>

                {row.nextExpiration ? (
                  <div className="mt-3 flex items-center gap-2 text-[11px]">
                    <i className="ri-calendar-event-line text-accent-700" />
                    <span className="text-foreground-600">
                      Vence {formatDate(row.nextExpiration)}
                    </span>
                    {days !== null ? (
                      <span
                        className={`rounded-full px-2 py-0.5 font-medium ${
                          days <= 0
                            ? "bg-red-100 text-red-700"
                            : days <= 30
                              ? "bg-accent-100 text-accent-800"
                              : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {days <= 0 ? "Vencido" : `${days} días`}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!pending}
        title="Eliminar producto"
        message={
          pending
            ? `Se eliminará "${pending.productName}" y todo su stock (${number(pending.quantity, 0)} ${pending.unit}). Esta acción no se puede deshacer.`
            : ""
        }
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}